import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getStatsSummary, getDailyStats } from '../api/stats';
import { getAllUsers } from '../api/users';
import Loader from '../components/Common/Loader';
import UserManagement from '../components/Admin/UserManagement';

const AdminPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dailyStats, setDailyStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryData, statsData, userData] = await Promise.all([
          getStatsSummary(),
          getDailyStats(30),
          getAllUsers()
        ]);
        
        setSummary(summaryData);
        setDailyStats(statsData);
        setUsers(userData);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return <Loader message="Loading admin data..." />;
  }

  return (
    <Box>
      <Box sx={{ my: 2 }}>
        <Typography variant="h4" gutterBottom>
          Admin Panel
        </Typography>
        
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Overview" />
            <Tab label="Statistics" />
            <Tab label="User Management" />
          </Tabs>
        </Paper>
        
        {/* Overview Tab */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              System Overview
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{summary?.total_images || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Images</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{summary?.submitted_images || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">Submitted Images</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{summary?.total_users || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Users</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">
                    {summary?.success_rate ? `${Math.round(summary.success_rate * 100)}%` : '0%'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Success Rate</Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Typography variant="h6" gutterBottom>
              Most Active Users
            </Typography>
            
            <Grid container spacing={2}>
              {summary?.most_active_users?.map((user) => (
                <Grid item xs={12} sm={6} md={4} key={user.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{user.username}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Images generated: {user.image_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {/* Statistics Tab */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Usage Statistics
            </Typography>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Image Generation Trends (Last 30 Days)
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={dailyStats}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="images_generated" 
                    name="Images Generated" 
                    stroke="#8884d8" 
                    activeDot={{ r: 8 }} 
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="images_submitted" 
                    name="Images Submitted" 
                    stroke="#82ca9d" 
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="active_users" 
                    name="Active Users" 
                    stroke="#ff7300" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Success Rate Trend
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={dailyStats}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 1]} tickFormatter={(value) => `${Math.round(value * 100)}%`} />
                  <Tooltip formatter={(value) => `${Math.round(value * 100)}%`} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="success_rate" 
                    name="Success Rate" 
                    stroke="#ff0000" 
                    activeDot={{ r: 8 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        )}
        
        {/* User Management Tab */}
        {tabValue === 2 && (
          <UserManagement />
        )}
      </Box>
    </Box>
  );
};

export default AdminPage;